/**
 * Types for Layer Creation and Digitization
 *
 * Defines interfaces for creating empty layers, digitizing features,
 * snapping configuration, and copy/paste operations.
 *
 * Validates: Requirements 47, 48, 49
 */

/** Supported geometry types for new layers */
export type LayerGeometryType = 'Point' | 'LineString' | 'Polygon';

/** Supported attribute field data types */
export type AttributeFieldType = 'string' | 'number' | 'boolean' | 'date';

/** Attribute field definition for a new layer */
export interface AttributeField {
  name: string;
  type: AttributeFieldType;
  defaultValue?: string | number | boolean | null;
}

/** Configuration for creating a new empty layer */
export interface CreateLayerConfig {
  name: string;
  geometryType: LayerGeometryType;
  crs: string;
  attributeFields: AttributeField[];
}

/** Supported CRS options for new layers */
export interface CRSOption {
  code: string;
  name: string;
  description: string;
}

/** Predefined CRS options */
export const SUPPORTED_CRS: CRSOption[] = [
  { code: 'EPSG:4326', name: 'WGS 84', description: 'Geographic (Latitude/Longitude)' },
  { code: 'EPSG:3857', name: 'Web Mercator', description: 'Pseudo-Mercator (Web Maps)' },
  {
    code: 'EPSG:32632',
    name: 'UTM Zone 32N',
    description: 'Universal Transverse Mercator Zone 32N',
  },
  {
    code: 'EPSG:32633',
    name: 'UTM Zone 33N',
    description: 'Universal Transverse Mercator Zone 33N',
  },
  {
    code: 'EPSG:32634',
    name: 'UTM Zone 34N',
    description: 'Universal Transverse Mercator Zone 34N',
  },
  {
    code: 'EPSG:32635',
    name: 'UTM Zone 35N',
    description: 'Universal Transverse Mercator Zone 35N',
  },
  {
    code: 'EPSG:32636',
    name: 'UTM Zone 36N',
    description: 'Universal Transverse Mercator Zone 36N',
  },
];

/** Snapping configuration */
export interface SnappingConfig {
  enabled: boolean;
  tolerance: number; // pixels
  snapToVertices: boolean;
  snapToEdges: boolean;
  snapToIntersections: boolean;
}

/** Default snapping configuration */
export const DEFAULT_SNAPPING_CONFIG: SnappingConfig = {
  enabled: false,
  tolerance: 10,
  snapToVertices: true,
  snapToEdges: true,
  snapToIntersections: false,
};

/** Feature clipboard entry for copy/paste */
export interface ClipboardFeature {
  geometry: GeoJSON.Geometry;
  properties: Record<string, any>;
  sourceLayerId: string;
  sourceGeometryType: LayerGeometryType;
}

/** Result of a paste operation */
export interface PasteResult {
  success: boolean;
  pastedCount: number;
  skippedCount: number;
  warnings: string[];
}

/** Digitization mode state */
export type DigitizationMode = 'idle' | 'drawing' | 'attribute-input';

/**
 * Gets default attribute fields based on geometry type
 */
export const getDefaultAttributeFields = (geometryType: LayerGeometryType): AttributeField[] => {
  const commonFields: AttributeField[] = [
    { name: 'name', type: 'string', defaultValue: `New ${geometryType}` },
    { name: 'description', type: 'string', defaultValue: '' },
    { name: 'category', type: 'string', defaultValue: 'General' },
    { name: 'status', type: 'string', defaultValue: 'Active' },
  ];

  if (geometryType === 'Polygon') {
    return [
      ...commonFields,
      { name: 'land_use', type: 'string', defaultValue: 'Residential' },
      { name: 'owner', type: 'string', defaultValue: '' },
      { name: 'area_sqm', type: 'number', defaultValue: 0 },
    ];
  }

  if (geometryType === 'LineString') {
    return [
      ...commonFields,
      { name: 'condition', type: 'string', defaultValue: 'Good' },
      { name: 'material', type: 'string', defaultValue: 'Asphalt' },
      { name: 'length_m', type: 'number', defaultValue: 0 },
    ];
  }

  return [...commonFields, { name: 'label', type: 'string', defaultValue: '' }];
};
